'use client'
import { useState, useEffect, useRef } from 'react'
import { useCurrentBreakpoint } from './useCurrentBreakpoint.hook'

/**
 * Hook to track mobile bottom navigation visibility based on scroll direction
 * Shows nav when scrolling down past 100px, hides when scrolling up
 * Accepts an optional forceVisible override to temporarily show the nav regardless of scroll state
 */
export const useMobileNavVisibility = (addSignal?: number) => {
  const { isMobile } = useCurrentBreakpoint()
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(false)
  const lastScrollYRef = useRef(0)

  // Each time addSignal increments, show the nav — scroll-up will still hide it
  useEffect(() => {
    if (addSignal && isMobile) {
      setIsBottomNavVisible(true)
    }
  }, [addSignal, isMobile])

  useEffect(() => {
    if (!isMobile) {
      setIsBottomNavVisible(false)
      return
    }

    // One check per frame; lastScrollY is a ref so the listener is attached once
    // instead of being re-subscribed after every 10px of scrolling.
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const scrollY = window.scrollY
        const scrollDifference = scrollY - lastScrollYRef.current

        // Only trigger if scrolled more than 10px to avoid jittery behavior
        if (Math.abs(scrollDifference) > 10) {
          if (scrollDifference > 0 && scrollY > 100) {
            // Scrolling down and past 100px - show navigation
            setIsBottomNavVisible(true)
          } else if (scrollDifference < 0) {
            // Scrolling up - hide navigation (overrides forceVisible)
            setIsBottomNavVisible(false)
          }
          lastScrollYRef.current = scrollY
        }
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [isMobile])

  return isBottomNavVisible
}
